import { createElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import * as LayoutUtils from './utils';
import type { Text, Container, LayoutPanels, BaseContainer, Counter } from './utils';
import { txt } from '../../../../locales/i18n';

export const counterLayoutPanels: LayoutPanels = {
	layoutName: txt('cms.layoutNames.counter'),
	panels: [
		{
			label: txt('cms.panelNames.layout container'),
			formKey: 'container',
			type: 'baseContainerPanel',
		},

		{
			label: txt('cms.panelNames.text container'),
			formKey: 'textContainer',
			type: 'containerPanel',
		},

		{
			label: txt('cms.panelNames.title'),
			formKey: 'title',
			type: 'textPanel',
		},

		{
			label: txt('cms.panelNames.counter container'),
			formKey: 'counterContainer',
			type: 'counterPanel',
		},
	],
};

type CounterLayoutProps = {
	container: BaseContainer;
	textContainer: Container;
	title: Text;
	counterContainer: Counter;
};

export const counterLayoutValues: CounterLayoutProps = {
	container: LayoutUtils.defaultBaseContainer,
	textContainer: LayoutUtils.defaultContainer,

	title: {
		color: '#000000',
		text: 'Title Tekst',
		isBold: false,
		isItalic: false,
		isUnderlined: false,
		textAlign: 'center',
		fontFamily: 'Raleway',
		htmlTag: 'h1',
		animation: LayoutUtils.defaultAnimation,
	},

	counterContainer: {
		textColor: '#000000',
		date: moment().add(7, 'days'),
	},
};

export const CounterLayout = (props: CounterLayoutProps) => {
	const [dateNow, setDateNow] = useState(moment());

	useEffect(() => {
		const interval = setInterval(() => setDateNow(moment()), 1000);
		return () => clearInterval(interval);
	}, []);

	const renderCounter = () => {
		const dateTarget = moment(props.counterContainer.date);
		const days = Math.max(dateTarget.diff(dateNow, 'days'), 0);
		const hours = Math.max(dateTarget.subtract(days, 'days').diff(dateNow, 'hours'), 0);
		const minutes = Math.max(dateTarget.subtract(hours, 'hours').diff(dateNow, 'minutes'), 0);
		const seconds = Math.max(
			dateTarget.subtract(minutes, 'minutes').diff(dateNow, 'seconds'),
			0
		);

		return (
			<div className="counter-container">
				<div className="counter-item">
					<span className="counter-item-value">{days}</span>
					<span>{txt('global.days')}</span>
				</div>

				<div className="counter-item">
					<span className="counter-item-value">{hours}</span>
					<span>{txt('global.hours')}</span>
				</div>

				<div className="counter-item">
					<span className="counter-item-value">{minutes}</span>
					<span>{txt('global.minutes')}</span>
				</div>

				<div className="counter-item">
					<span className="counter-item-value">{seconds}</span>
					<span>{txt('global.seconds')}</span>
				</div>
			</div>
		);
	};

	const header = createElement(props.title.htmlTag, {
		className: 'header',
		dangerouslySetInnerHTML: { __html: LayoutUtils.formatText(props.title.text) },
		...LayoutUtils.getAnimationProps(props.title.animation),
	});

	return (
		<CounterLayoutPreviewStyled
			styledProps={props}
			{...LayoutUtils.getAnimationProps(props.container.animation)}
		>
			<div
				className="text-container"
				{...LayoutUtils.getAnimationProps(props.textContainer.animation)}
			>
				{props.title.text !== '' && header}
				{renderCounter()}
			</div>
		</CounterLayoutPreviewStyled>
	);
};

const CounterLayoutPreviewStyled = styled.div<{ styledProps: CounterLayoutProps }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4em;

	background-repeat: no-repeat;
	background-color: ${({ styledProps }) => styledProps.container.backgroundColor};
	background-size: ${({ styledProps }) => styledProps?.container?.coverFit ?? 'cover'};
	background-position: ${({ styledProps }) => styledProps?.container?.coverPosition ?? 'center'};
	background-image: ${({ styledProps }) => {
		if (styledProps.container.coverSource === '') return 'none';
		return `url(${LayoutUtils.getImageSource(styledProps.container.coverSource)})`;
	}};

	min-height: ${({ styledProps }) => (styledProps.container.fullscreen ? '100dvh' : 'auto')};

	.text-container {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 3rem;
		width: auto;
		gap: 1rem;

		${({ styledProps }) => LayoutUtils.getContainerStyle(styledProps.textContainer)};
		width: 640px;

		.header {
			${({ styledProps }) => LayoutUtils.getTextStyle(styledProps.title)};
		}

		.counter-container {
			color: ${({ styledProps }) => styledProps.counterContainer.textColor};
		}
	}

	.counter-container {
		max-width: 800px;
		color: ${({ styledProps }) => styledProps.counterContainer.textColor};
		display: grid;
		grid-template-columns: repeat(4, 1fr);

		.counter-item {
			display: flex;
			flex-direction: column;
			align-items: center;
		}

		.counter-item-value {
			font-weight: bold;
			font-size: 3rem;
		}
	}
`;
